/**
 * @fileoverview Primary/secondary action button atom.
 * @description The button styles used for page-level actions, with a built-in loading spinner so
 * callers only need to toggle isLoading and their button text. Has both a size prop (two
 * heights/type scales) and a variant prop (a filled gradient "primary" look, built from the
 * primary/primary-dark color tokens rather than a raw hex gradient string; an outlined white
 * "secondary" look; or "secondary-accent", the same outlined box with primary-colored text, for a
 * secondary action that should still read as the more inviting of two side-by-side choices).
 * className is deliberately excluded rather than left as an overridable prop, matching
 * Input/Label/IconButton, so two utility classes never compete to set the same CSS property;
 * external spacing (margin) belongs on the caller's wrapper, never here.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  isLoading?: boolean
  size?: 'lg' | 'md'
  variant?: 'primary' | 'secondary' | 'secondary-accent'
}

const SIZE_CLASS_NAMES: Record<NonNullable<ButtonProps['size']>, string> = {
  lg: 'h-[54px] rounded-[11px] px-6 text-button-lg',
  md: 'h-12 rounded-[10px] px-5 text-md',
}

const VARIANT_CLASS_NAMES: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary:
    'bg-linear-to-br from-primary to-primary-dark text-white shadow-[0_4px_16px_rgba(37,99,235,0.32)]',
  secondary: 'border border-input-border bg-white text-ink',
  'secondary-accent': 'border border-input-border bg-white text-primary',
}

/**
 * @component
 * @param {ButtonProps} props - Standard button attributes plus isLoading/size/variant.
 * @param {boolean} [props.isLoading] - Disables the button and shows a spinner ahead of its text.
 * @param {'lg' | 'md'} [props.size] - "lg" (default, 54px/17px text) or "md" (48px/15px text).
 * @param {'primary' | 'secondary' | 'secondary-accent'} [props.variant] - "primary" (default,
 * filled gradient), "secondary" (outlined white, dark text), or "secondary-accent" (outlined
 * white, primary-colored text).
 */
function Button({
  isLoading = false,
  size = 'lg',
  variant = 'primary',
  disabled,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      className={`flex w-full cursor-pointer items-center justify-center gap-2 font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-70 ${SIZE_CLASS_NAMES[size]} ${VARIANT_CLASS_NAMES[variant]}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <span
          className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
          aria-hidden="true"
        />
      )}
      {children}
    </button>
  )
}

export default Button
